import React, { useContext, useEffect, useState } from "react";
import { ClientConfigurationContext } from "../../context/ClientConfigurationContextProvider";
import Loader, { Types } from "../gdk/Loader";
import { capitalizeFirst, ReportPages } from "./ReportsHelper";
import { UserContext } from "../../context/UserContextProvider";
import Layout from "../../components/Layout";
import ReportsSideMenu from "./ReportsSideMenu";
import ReportOverviewPage from "./Overview/ReportOverviewPage";
import ReportDetailedPage from "./DetailedView/ReportDetailedPage";
import { AgentAccessContext } from "../../context/AgentAccessContextProvider";
import { getFilterOptions, getFilterOptionsInternal } from "../../api/external-reports";
import ReportSweepsPage from "./SweepsReportPage";
import ReportErrorPage from "./ReportErrorPage";

export default function ReportPage({ page, substituteAgentInfo }) {
    const user = useContext(UserContext);
    const agentAccess = useContext(AgentAccessContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const [currentPage, setCurrentPage] = useState(page ?? ReportPages.Overview);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(true);

    const [overviewData, setOverviewData] = useState(null);
    const [detailReportData, setDetailReportData] = useState(null);
    const [sweepsData, setSweepsData] = useState(null);

    const [filterOptions, setFilterOptions] = useState(null);
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
    const [maxPolicyActiveDate, setMaxPolicyActiveDate] = useState(null);
    const [minPolicyActiveDate, setMinPolicyActiveDate] = useState(null);

    const [overviewSelectedDateRange, setOverviewSelectedDateRange] = useState();
    const [overviewAppliedFilters, setOverviewAppliedFilters] = useState({});
    const [detailedSelectedDateRange, setDetailedSelectedDateRange] = useState();
    const [detailedAppliedFilters, setDetailedAppliedFilters] = useState({});
    const [sweepsSelectedDateRange, setSweepsSelectedDateRange] = useState('All In');
    const [sweepsAppliedFilters, setSweepsAppliedFilters] = useState({});

    useEffect(() => {
        if (!filterOptions && !hasAttemptedLoad) {
            loadFilterOptions();
        }
    }, []);

    function loadFilterOptions() {
        const handleSuccess = (response) => {
            setHasAttemptedLoad(true);
            if (response?.Status === 3) {
                let options = response.Domain;
                if (options.AgentProfiles?.length > 0) {
                    options.AgentProfiles = options.AgentProfiles.map(agent => `${agent.Id}- ${capitalizeFirst(agent.FirstName)} ${capitalizeFirst(agent.LastName)}`);
                }
                setFilterOptions(
                    Object.fromEntries(
                        Object.entries(options).filter(([key]) => key !== 'MaxReportingDate' && key !== 'AgencyMinReportingDate')
                    ));
                setMaxPolicyActiveDate(new Date(options.MaxReportingDate));
                setMinPolicyActiveDate(new Date(options.AgencyMinReportingDate));
            }
            setLoading(false);
        }

        const handleError = () => {
            setLoading(false);
            setHasAttemptedLoad(true);
        }

        substituteAgentInfo?.id
            ? getFilterOptionsInternal(substituteAgentInfo.id, response => handleSuccess(response), handleError)
            : getFilterOptions(response => handleSuccess(response), handleError)
    }

    if (loading) { return <Loader type={Types.Section} />; }
    if (!agentAccess || !filterOptions) { return <ReportErrorPage />; }

    return (agentAccess && filterOptions &&
        <Layout>
            {((clientConfiguration.enableReports && agentAccess.externalReportsEnabled) || substituteAgentInfo) && (
                <div className={`side-menu-padding ${clientConfiguration.enablePrintReportButton ? '' : 'print-disabled'}`} style={clientConfiguration.externalReportsSideMenuBugFix ? { transition: "padding-left 0.3s ease", paddingLeft: drawerOpen ? "295px" : "50px" } : {}}>
                    <ReportsSideMenu
                        open={drawerOpen}
                        toggleOpen={() => setDrawerOpen(!drawerOpen)}
                        page={currentPage}
                        setPage={setCurrentPage}
                        showSweeps={!!(clientConfiguration.enableSweepsReportPage || substituteAgentInfo)}
                    />
                    {currentPage === ReportPages.Overview &&
                        <ReportOverviewPage
                            user={user}
                            substituteAgentInfo={substituteAgentInfo}
                            data={overviewData}
                            setData={setOverviewData}
                            dataLastUpdatedDate={maxPolicyActiveDate}
                            dataStartDate={minPolicyActiveDate}
                            filterOptions={filterOptions}
                            selectedDateRange={overviewSelectedDateRange}
                            setSelectedDateRange={setOverviewSelectedDateRange}
                            appliedFilters={overviewAppliedFilters}
                            setAppliedFilters={setOverviewAppliedFilters}
                        />
                    }
                    {currentPage === ReportPages.Detailed &&
                        <ReportDetailedPage
                            user={user}
                            substituteAgentInfo={substituteAgentInfo}
                            data={detailReportData}
                            setData={setDetailReportData}
                            dataLastUpdatedDate={maxPolicyActiveDate}
                            dataStartDate={minPolicyActiveDate}
                            filterOptions={filterOptions}
                            selectedDateRange={detailedSelectedDateRange}
                            setSelectedDateRange={setDetailedSelectedDateRange}
                            appliedFilters={detailedAppliedFilters}
                            setAppliedFilters={setDetailedAppliedFilters}
                        />
                    }
                    {currentPage === ReportPages.Sweeps && (clientConfiguration.enableSweepsReportPage || substituteAgentInfo) &&
                        <ReportSweepsPage
                            user={user}
                            substituteAgentInfo={substituteAgentInfo}
                            data={sweepsData}
                            setData={setSweepsData}
                            dataLastUpdatedDate={maxPolicyActiveDate}
                            dataStartDate={minPolicyActiveDate}
                            filterOptions={filterOptions}
                            selectedDateRange={sweepsSelectedDateRange}
                            setSelectedDateRange={setSweepsSelectedDateRange}
                            appliedFilters={sweepsAppliedFilters}
                            setAppliedFilters={setSweepsAppliedFilters}
                        />
                    }
                </div >
            )}
        </Layout >
    );
}